import { ReactNode } from 'react'
import { Logo } from '@/shared/components/ui/Logo'
import { appConfig } from '@/lib/appConfig'
import type { UnitContext } from '@/modules/units/contexts/UnitContextTypes'
import { useTheme } from '@/shared/contexts/ThemeContext'

interface AuthLayoutProps {
  children?: ReactNode
  title?: string
  subtitle?: string
  isLoading?: boolean
}

export function AuthLayout({ 
  children, 
  title = appConfig.name, 
  subtitle,
  isLoading = false
}: AuthLayoutProps) {
  // If a context is fixed in the environment, use it to render the login logo
  // Accept legacy NEXT_PUBLIC_APP_CONTEXT as well for local setups
  const envContext = (
    (import.meta.env.VITE_PUBLIC_APP_CONTEXT as UnitContext) ||
    (import.meta.env.NEXT_PUBLIC_APP_CONTEXT as UnitContext) ||
    undefined
  )
  const { theme } = useTheme()
  
  const isDark = theme === 'dark'
  
  return (
    <div className={`flex min-h-screen items-center justify-center px-4 py-12 ${
      isDark 
        ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700' 
        : 'bg-gradient-to-br from-green-700 via-green-600 to-green-500'
    }`}>
      <div className="w-full max-w-md">
        <div className={`rounded-3xl px-8 py-12 shadow-2xl ${
          isDark
            ? 'bg-gradient-to-b from-slate-700 to-slate-800'
            : 'bg-gradient-to-b from-gray-100 to-gray-200'
        }`}>
          {/* Logo */}
          <div className="mb-8 flex justify-center">
            <Logo context={envContext} />
          </div>

          {/* Título */}
          <div className="mb-8 text-center">
            <h1 className={`text-3xl font-bold ${isDark ? 'text-slate-100' : 'text-gray-800'}`}>{title}</h1>
            {subtitle && (
              <p className={`mt-2 ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>{subtitle}</p>
            )}
          </div>

          {/* Conteúdo */}
          {isLoading ? (
            <div className="flex justify-center">
              <div className={`h-12 w-12 animate-spin rounded-full border-4 border-t-transparent ${isDark ? 'border-emerald-500' : 'border-green-600'}`}></div>
            </div>
          ) : (
            children
          )}
        </div>
      </div>
    </div>
  )
}
